import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import useSWR, { mutate as globalMutate } from 'swr';
import { TagSpecDto } from '@ampsec/platform-client';
import { fetchAllTagSpecsCatalog } from 'Apis/tagSpecsCatalog';
import {
    clearTagSpecsCatalog,
    readTagSpecsCatalog,
    TAG_SPECS_CATALOG_TTL_MS,
    writeTagSpecsCatalog,
} from 'Core-utils/helpers/tagSpecsCatalogCache';
import { RootState } from 'Rdx/store';

/** SWR cache namespace for tag spec catalog fetches (paired with tenant id and `includeGlobals`). */
export const TAG_SPECS_CATALOG_SWR_KEY = 'tagSpecsCatalog';

/** Options for {@link useTagSpecsCatalog}. */
export type UseTagSpecsCatalogOptions = {
    /** When true (default), fetches tenant specs plus platform globals via `includeGlobals` on the API. */
    includeGlobals?: boolean;
    /** When false, skips fetch (e.g. feature-gated UI). Defaults to true. */
    enabled?: boolean;
};

/**
 * Builds the SWR key for a tenant catalog subscription, or null when `tid` is missing.
 *
 * @param tid - Selected tenant id from auth state.
 * @param includeGlobals - Whether this subscription uses the merged catalog shape.
 * @returns Tuple key for `useSWR`, or null if there is no tenant.
 */
function swrKey(tid: string | undefined, includeGlobals: boolean): readonly [string, string, boolean] | null {
    if (!tid) {
        return null;
    }
    return [TAG_SPECS_CATALOG_SWR_KEY, tid, includeGlobals] as const;
}

/**
 * Loads the tag spec catalog for the selected tenant with SWR dedupe and localStorage fallback.
 *
 * Uses a 1-hour dedupe window, revalidates on focus when stale, and persists full catalogs
 * (`includeGlobals: true`) to localStorage after successful fetches.
 *
 * @param options - {@link UseTagSpecsCatalogOptions}
 * @returns `tagSpecs` list, loading/error state, and `refresh` to re-run SWR for this key.
 */
export function useTagSpecsCatalog(options: UseTagSpecsCatalogOptions = {}) {
    const { includeGlobals = true, enabled = true } = options;
    const tid = useSelector((state: RootState) => state.auth.selectedTenant?.id) as string | undefined;
    const key = enabled ? swrKey(tid, includeGlobals) : null;

    const fallback = tid ? readTagSpecsCatalog(tid, includeGlobals) ?? undefined : undefined;

    const { data, error, isLoading, mutate } = useSWR<TagSpecDto[]>(
        key,
        () => fetchAllTagSpecsCatalog(tid!, includeGlobals),
        {
            dedupingInterval: TAG_SPECS_CATALOG_TTL_MS,
            revalidateOnFocus: true,
            fallbackData: fallback,
        },
    );

    useEffect(() => {
        if (tid && data && includeGlobals) {
            writeTagSpecsCatalog(tid, includeGlobals, data);
        }
    }, [tid, includeGlobals, data]);

    return {
        tagSpecs: data ?? fallback ?? [],
        isLoading: Boolean(key) && isLoading && !data && !fallback,
        error,
        refresh: () => mutate(),
    };
}

/**
 * Warms the catalog cache before navigation (tenant switch, Engagement Studio, sign-in).
 *
 * Reads localStorage first; on miss, fetches paginated `GET /tag_specs` and seeds SWR + storage.
 *
 * @param tid - Tenant to load catalog for.
 * @param includeGlobals - When true (default), request merged tenant + global specs.
 * @returns Resolved tag spec list (from cache or network).
 */
export async function prefetchTagSpecsCatalog(tid: string, includeGlobals = true): Promise<TagSpecDto[]> {
    const key = swrKey(tid, includeGlobals);
    const cached = readTagSpecsCatalog(tid, includeGlobals);
    if (cached) {
        if (key) {
            await globalMutate(key, cached, { revalidate: false });
        }
        return cached;
    }

    const specs = await fetchAllTagSpecsCatalog(tid, includeGlobals);
    writeTagSpecsCatalog(tid, includeGlobals, specs);
    if (key) {
        await globalMutate(key, specs, { revalidate: false });
    }
    return specs;
}

/**
 * Drops cached catalog data and triggers SWR revalidation (e.g. after CRUD or sign-out).
 *
 * @param tid - When set, clears storage and revalidates keys for that tenant only; when omitted, clears all catalog keys.
 * @returns void
 */
export function invalidateTagSpecsCatalog(tid?: string): void {
    clearTagSpecsCatalog(tid);
    globalMutate(
        (key) => Array.isArray(key) && key[0] === TAG_SPECS_CATALOG_SWR_KEY && (tid ? key[1] === tid : true),
        undefined,
        { revalidate: true },
    );
}
